import type { Item } from "@workspace/api-client-react";
import { formatMeasurement, type MeasurementUnit } from "./measurements";

const FORMULA_SIGILS = /^[=+\-@|%\t\r]/;

function sanitizeCsvCell(value: string | number | null | undefined): string {
  const str = String(value ?? "");
  if (FORMULA_SIGILS.test(str)) {
    return `\t${str}`;
  }
  return str;
}

export function itemsToCsv(items: Item[]): string {
  const headers = [
    "ID",
    "Name",
    "Item Type",
    "Parent Set IDs",
    "Parent Set Order",
    "Child IDs",
    "Child Order",
    "Inventory Status",
    "Manufacturer",
    "Blade Style",
    "Handle Material",
    "Blade Material",
    "Generation",
    "Blade Design",
    "Blade Treatment",
    "Handle Treatment",
    "Grind Angle (degrees)",
    "Blade Length",
    "Blade Width",
    "Handle Length",
    "Measurement Unit",
    "Purchase Price",
    "Purchase Date",
    "Hand Forged",
    "Daniel Forged",
    "Original Box",
    "Original Paperwork",
    "Limited Edition",
    "Edition Number",
    "Edition Total",
    "Tomahawk / Axe",
    "Blade Comments",
    "Sheath Comments",
    "General Notes",
  ];

  const rows = items.map((item) => {
    const unit: MeasurementUnit = item.preferredDisplayUnit ?? "mm";
    return [
      sanitizeCsvCell(item.id),
      sanitizeCsvCell(item.name),
      sanitizeCsvCell(item.itemType),
      sanitizeCsvCell(
        (item.parentSets ?? [])
          .slice()
          .sort((a, b) => a.position - b.position || a.id - b.id)
          .map((parent) => parent.id)
          .join(";"),
      ),
      sanitizeCsvCell(
        (item.parentSets ?? [])
          .slice()
          .sort((a, b) => a.position - b.position || a.id - b.id)
          .map((parent) => parent.position)
          .join(";"),
      ),
      sanitizeCsvCell(
        (item.children ?? [])
          .slice()
          .sort((a, b) => a.position - b.position || a.id - b.id)
          .map((child) => child.id)
          .join(";"),
      ),
      sanitizeCsvCell(
        (item.children ?? [])
          .slice()
          .sort((a, b) => a.position - b.position || a.id - b.id)
          .map((child) => child.position)
          .join(";"),
      ),
      item.inventoryStatus === "sold" ? "Sold" : "In-Inventory",
      sanitizeCsvCell(item.manufacturer || ""),
      sanitizeCsvCell(item.bladeStyle || ""),
      sanitizeCsvCell(item.handleMaterial || ""),
      sanitizeCsvCell(item.bladeMaterial || ""),
      sanitizeCsvCell(item.generation || ""),
      sanitizeCsvCell(item.bladeDesign || ""),
      sanitizeCsvCell(item.bladeTreatment || ""),
      sanitizeCsvCell(item.handleTreatment || ""),
      item.grindAngle?.toString() || "",
      formatMeasurement(item.bladeLengthMm, unit) || "",
      formatMeasurement(item.bladeWidthMm, unit) || "",
      formatMeasurement(item.handleLengthMm, unit) || "",
      unit,
      item.purchasePrice?.toString() || "",
      item.purchaseDate || "",
      item.handForged ? "Yes" : "No",
      item.danielForged ? "Yes" : "No",
      item.originalBox ? "Yes" : "No",
      item.originalPaperwork ? "Yes" : "No",
      item.limitedEdition ? "Yes" : "No",
      item.editionNumber?.toString() || "",
      item.editionTotal?.toString() || "",
      item.isAxe ? "Yes" : "No",
      sanitizeCsvCell(
        (item.bladeComments || item.extendedDescription || "").replace(
          /\n/g,
          " ",
        ),
      ),
      sanitizeCsvCell((item.sheathComments || "").replace(/\n/g, " ")),
      sanitizeCsvCell((item.generalNotes || "").replace(/\n/g, " ")),
    ];
  });

  return [
    headers.map((h) => `"${h}"`).join(","),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    ),
  ].join("\n");
}

export function downloadBlob(blob: Blob, filename: string) {
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function exportItemsToCsv(items: Item[], filename: string) {
  if (!items.length) return;
  const csvContent = itemsToCsv(items);
  downloadBlob(
    new Blob([csvContent], { type: "text/csv;charset=utf-8;" }),
    filename,
  );
}
